export type ColumnType = 'string' | 'number' | 'date' | 'boolean';
export type DateGranularity = 'month' | 'week' | 'quarter';

// A serve-time DERIVED column: absent from the fetched Parquet, computed by the
// DuckDB-WASM compiler from a NORMAL source column against the current Pacific
// business day. v1 kind: 'days_since' (integer day count from the source DATE
// column to today). Absent for ordinary (materialized) columns.
export interface DerivedFrom {
  kind: string;
  column: string;
}

export interface DatasetColumn {
  name: string;
  label: string;
  type: ColumnType;
  default_buckets?: number[];
  default_granularity?: DateGranularity;
  derived_from?: DerivedFrom;
}

export interface DatasetRelationship {
  target: string;
  label: string;
  local_key: string;
  target_key: string;
  cardinality: string; // v1: only 'many_to_one' is compilable
}

export interface DatasetManifestEntry {
  key: string;
  label: string;
  /**
   * Key of the dataset whose permission umbrella this one belongs to; absent
   * means it stands alone. Saved reports are badged with the umbrella's label
   * ("Episodes"), while the builder's picker keeps this dataset's own label.
   */
  umbrella_key?: string;
  version: number;
  columns: DatasetColumn[];
  relationships?: DatasetRelationship[];
  /**
   * Axes a windowed `scope=primary` can actually be pruned on for this caller
   * (zone-mapped AND served). Advisory: the server still accepts any axis and
   * fails open to the full dataset on one it cannot prune, so this only decides
   * whether we mint a per-window client cache entry that would hold identical
   * bytes anyway.
   *
   * Optional because an older server omits it: `undefined` means "no
   * information, keep the legacy derivation" (window on any date column), while
   * `[]` means "nothing on this dataset is prunable".
   */
  prunable_axes?: string[];
}

export interface ReportingManifest {
  datasets: DatasetManifestEntry[];
}

export type BucketTransform = { type: 'bucket'; edges: number[] };
export type DateTruncTransform = { type: 'date_trunc'; unit: DateGranularity };
export type DimensionTransform = BucketTransform | DateTruncTransform;

export interface GroupBy {
  column: string;
  transform?: DimensionTransform;
}

export type Agg = 'count' | 'sum' | 'avg' | 'min' | 'max';

export interface Measure {
  agg: Agg;
  column?: string; // undefined for count
}

export type FilterOp =
  | 'eq'
  | 'neq'
  | 'in'
  | 'contains'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'between'
  | 'is_null'
  | 'not_null'
  | 'relative_date';

export interface ReportFilter {
  column: string;
  op: FilterOp;
  value?: unknown; // op-specific: scalar | [lo, hi] | values[] | { last_days: number } | { next_days: number }
}

export type ReportView = 'table' | 'pivot' | 'bar';

// Authored chart config for a DETAIL report (only meaningful when
// `group_bys` is empty — a grouped report charts via its own group_bys/
// measures instead). `column` is required iff `agg === 'sum'` (omitted for
// `count`). The backend validates that `group_by`/`column` are among the
// definition's selected `columns`.
export interface ReportChartConfig {
  group_by: string;
  agg: 'sum' | 'count';
  column?: string;
}

export interface ReportDefinition {
  // 1 = primary-only; 2 = references related datasets (dotted refs). The
  // backend enforces the equivalence; stampDefinitionVersions keeps it true.
  schema_version: 1 | 2;
  dataset_key: string;
  dataset_version: number;
  // Per-related-dataset version stamps. Backend-enforced contract: present
  // iff the definition is joined, keys exactly the referenced related
  // datasets (stampDefinitionVersions keeps it true). Drift is detected
  // structurally at open (live manifests) and at run time (the runner's
  // extract-schema gate) — see useReportRunner.
  related_versions?: Record<string, number>;
  // Explicit, ordered list of related dataset keys joined into the base — the
  // user adds a join like they add a filter, and its columns then become
  // selectable. Each key must be a declared many-to-one relationship target of
  // the base dataset AND permitted for the caller. A dataset stays joined (and
  // is LEFT JOINed by the compiler) even when no column from it is currently
  // selected — the join is explicit intent, not a byproduct of a column ref.
  // Optional for back-compat: legacy definitions saved by the per-column UI
  // omit it; backfillJoinedDatasets derives it from their dotted refs on load.
  // `undefined` = not yet backfilled (legacy); `[]` = explicitly no joins.
  joined_datasets?: string[];
  columns: string[]; // detail view; empty = all manifest columns
  group_bys: GroupBy[];
  measures: Measure[];
  pivot_column?: GroupBy | null;
  filters: ReportFilter[];
  sort: { by: string; dir: 'asc' | 'desc' }[];
  view: ReportView;
  // Table-view subtotal rows per leading group-by value. Optional and absent
  // by default (legacy definitions omit it); meaningful only with >= 2
  // group-bys and an additive (count/sum) measure — the table gates on both.
  subtotals?: boolean;
  // Authored chart config for a DETAIL report (no group_bys); optional and
  // absent by default. `validateDefinition` drops it when stale (group_by/
  // column no longer among the selected columns), which normalizes `view`
  // back to 'table' for a detail report on open.
  chart?: ReportChartConfig | null;
  // Opt-in zero-filled BUSINESS-DAY spine for exact-date grouped reports.
  // Zero-fills Monday–Friday within the bounded window; any date actually
  // present in the aggregate — including weekends — always renders, so no
  // dollar is ever dropped by the spine. Optional and absent by default;
  // ignored unless compileReport's full trigger holds (single untransformed
  // date group-by, all-additive measures, only bound-representable filters on
  // the grouped column, and a resolved window no wider than
  // MAX_DATE_CATEGORIES CALENDAR days) — otherwise the report renders exactly
  // as it would with gap_fill absent. An empty window still renders "no
  // results", never a grid of $0 rows.
  gap_fill?: boolean;
}

export interface ReportListItem {
  id: string | null;
  system_key: string | null;
  name: string;
  description: string | null;
  dataset_key: string;
  source: 'system' | 'agency';
  created_by: string | null;
  created_by_name: string | null;
  created_at: string | null;
  updated_at: string | null;
  definition: ReportDefinition;
  visibility: 'private' | 'agency' | null;
}

export interface ReportDefinitionPayload {
  name: string;
  description?: string | null;
  dataset_key: string;
  definition: ReportDefinition;
  visibility?: 'private' | 'agency';
}

// Highest definition schema_version this build understands. The builder
// refuses to open (read-only-refuse, not silently degrade) any definition
// newer than this, so a future shape change can't be destructively re-saved
// by an out-of-date tab.
export const SUPPORTED_SCHEMA_VERSION = 2;

export function emptyDefinition(
  datasetKey: string,
  datasetVersion: number
): ReportDefinition {
  return {
    schema_version: 1,
    dataset_key: datasetKey,
    dataset_version: datasetVersion,
    columns: [],
    group_bys: [],
    measures: [],
    pivot_column: null,
    filters: [],
    sort: [],
    view: 'table',
  };
}
